import React, { useState, useEffect, useCallback } from 'react';
import { Alert } from 'react-native';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { getAllJobs, getStateFromStorage, storeState } from '../utils';
import { Application, ApplicationStatus, JobAlert, JobType, ResumeDocument, TemplateId } from '../types';
import JobsContext from './JobsContext';
import {
  addEntitlementListener,
  checkEntitlement,
  getRemoveAdsPackageWithReason,
  initializeRevenueCat,
  purchaseRemoveAds as purchaseRemoveAdsSdk,
  resetRcUserForTesting,
  restorePurchases as restorePurchasesSdk,
} from '../lib/revenueCat';
import { track } from '../lib/analytics';
import useAuthUser from '../lib/auth/useAuthUser';
import auth from '../lib/auth';
import { deleteUserState, readUserState, writeUserState } from '../lib/sync/firestoreStore';
import { SyncableState, mergeResumes, mergeApplications, mergeAlerts } from '../lib/sync/merge';
import { syncOnSignIn } from '../lib/sync/syncEngine';
import notifications from '../lib/notifications';

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptyResume = (
  id: string,
  name: string,
  template: TemplateId,
  now: string
): ResumeDocument => ({
  id,
  name,
  template,
  createdAt: now,
  updatedAt: now,
  personalInfo: {
    fullName: '',
    title: '',
    email: '',
    phoneCountryCode: '',
    phone: '',
    location: '',
    website: '',
    summary: '',
    photo: '',
  },
  experience: [],
  education: [],
  skills: [],
  languages: [],
  links: [],
  personalization: {
    colorMode: 'light',
    fontFamily: 'sans',
    fontScale: { title: 1, subtitle: 1, body: 1 },
  },
});

interface GlobalStateProviderProps {
  children: React.ReactNode;
}

export function GlobalStateProvider({ children }: GlobalStateProviderProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<JobType[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [applied, setApplied] = useState<string[]>([]);
  const [applications, setApplications] = useState<Record<string, Application>>({});
  const [alerts, setAlerts] = useState<JobAlert[]>([]);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean>(false);
  const [themeMode, setThemeModeState] = useState<'light' | 'dark'>('light');
  const [language, setLanguageState] = useState<string>('');
  // Last-write-wins clock for scalar settings (theme/language). '' = never
  // changed locally, so a remote backup wins on a fresh device. Bumped only by
  // the user-facing setters below — NOT by the sign-in restore.
  const [settingsUpdatedAt, setSettingsUpdatedAt] = useState<string>('');
  const [hideAds, setHideAds] = useState<boolean>(false);
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState<boolean>(false);
  const [removeAdsEntitled, setRemoveAdsEntitled] = useState<boolean>(false);
  const [removeAdsPurchasing, setRemoveAdsPurchasing] = useState<boolean>(false);
  const [removeAdsPriceString, setRemoveAdsPriceString] = useState<string | null>(null);
  const [removeAdsLastError, setRemoveAdsLastError] = useState<string | null>(null);
  const removeAdsPackageRef = React.useRef<
    Awaited<ReturnType<typeof getRemoveAdsPackageWithReason>>['package']
  >(null);
  const [resumes, setResumes] = useState<ResumeDocument[]>([]);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!hydrated) return;
    storeState({
      data,
      keys,
      applied,
      applications,
      alerts,
      onboardingCompleted,
      themeMode,
      language,
      hideAds,
      removeAdsEntitled,
      resumes,
      settingsUpdatedAt,
      disclaimerAcknowledged,
    });
  }, [
    data,
    keys,
    applied,
    applications,
    alerts,
    onboardingCompleted,
    themeMode,
    language,
    hideAds,
    removeAdsEntitled,
    resumes,
    settingsUpdatedAt,
    disclaimerAcknowledged,
    hydrated,
  ]);

  // User-facing settings setters bump the LWW clock so cloud sync knows these
  // values were changed on this device (and when).
  const setThemeMode = useCallback((mode: 'light' | 'dark') => {
    setThemeModeState(mode);
    setSettingsUpdatedAt(new Date().toISOString());
  }, []);

  const setLanguage = useCallback((lang: string) => {
    setLanguageState(lang);
    setSettingsUpdatedAt(new Date().toISOString());
    if (lang) i18n.changeLanguage(lang);
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const jobsData: JobType[] = (await getAllJobs()) || [];
      if (jobsData.length > 0) {
        setData(jobsData);
        setError(false);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error refreshing jobs:', err);
      setError(true);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const stored = await getStateFromStorage();
        setData(stored.data);
        setKeys(stored.keys);
        setApplied(stored.applied);
        setApplications(stored.applications);
        setAlerts(stored.alerts);
        setOnboardingCompleted(stored.onboardingCompleted);
        setThemeModeState(stored.themeMode);
        setLanguageState(stored.language);
        setHideAds(stored.hideAds);
        setRemoveAdsEntitled(stored.removeAdsEntitled);
        setResumes(stored.resumes);
        setSettingsUpdatedAt(stored.settingsUpdatedAt);
        setDisclaimerAcknowledged(stored.disclaimerAcknowledged);
        if (stored.language) i18n.changeLanguage(stored.language);
        setHydrated(true);

        if (stored.data.length === 0) {
          refresh();
        }
      } catch (err) {
        console.error('Error loading initial state:', err);
        setError(true);
        setHydrated(true);
      }
    };

    loadInitialState();
  }, [refresh]);

  const authUser = useAuthUser();
  // The uid whose remote backup we've already pulled + merged into local this
  // session. The mirror MUST NOT push until this matches the signed-in uid,
  // otherwise a freshly-reset (empty) local state would overwrite the user's
  // cloud backup — the data-loss bug. Null when signed out / not yet synced.
  const [syncedUid, setSyncedUid] = useState<string | null>(null);
  // Set while an account deletion is in flight so the debounced mirror can't
  // re-create the just-deleted cloud doc from the cleared (empty) local state
  // during the brief window before the auth listener reports sign-out. Reset
  // once the user is actually signed out (or if the deletion fails).
  const deletingRef = React.useRef(false);

  // On sign-in: pull the remote copy, merge it with local (never loses data —
  // mergeSyncable unions favorites/applied and keeps newest resumes/apps), and
  // apply the result to local state. Runs once per uid, BEFORE the mirror is
  // unlocked. A null read (no doc / read failure) is treated as "no remote
  // copy", so we never wipe the cloud on a transient error.
  useEffect(() => {
    if (!authUser) {
      // Signed out — require a fresh pull before the next sign-in can mirror,
      // and clear any in-flight deletion guard now that we're actually out.
      setSyncedUid(null);
      deletingRef.current = false;
      return;
    }
    if (!hydrated || syncedUid === authUser.uid) return;

    let cancelled = false;
    (async () => {
      const local: SyncableState = {
        resumes,
        keys,
        applied,
        applications,
        alerts,
        themeMode,
        language,
        // The REAL settings clock (not "now"), so a fresh device with untouched
        // settings ('') lets the remote backup win the scalar merge.
        updatedAt: settingsUpdatedAt,
      };
      try {
        const merged = await syncOnSignIn(
          { read: readUserState, write: writeUserState },
          authUser.uid,
          local
        );
        if (cancelled) return;
        // Re-merge against CURRENT state (not the captured snapshot) so any
        // favorites / applications / resumes the user added during the async
        // sign-in window aren't clobbered by the pre-window merge result.
        // In the no-concurrent-edit case these collapse to `merged.*`.
        setResumes((prev) => mergeResumes(prev, merged.resumes));
        setKeys((prev) => Array.from(new Set([...prev, ...merged.keys])));
        setApplied((prev) => Array.from(new Set([...prev, ...merged.applied])));
        setApplications((prev) => mergeApplications(prev, merged.applications));
        setAlerts((prev) => mergeAlerts(prev, merged.alerts));
        // Apply settings via the state setters directly so we DON'T bump the
        // local clock — adopt the merged value and its timestamp as-is.
        if (merged.themeMode) setThemeModeState(merged.themeMode);
        if (typeof merged.language === 'string') {
          setLanguageState(merged.language);
          if (merged.language) i18n.changeLanguage(merged.language);
        }
        if (typeof merged.updatedAt === 'string') setSettingsUpdatedAt(merged.updatedAt);
        // Unlock the mirror ONLY after a successful pull+merge for this uid, so
        // a failed sync can never let the debounced mirror push a local-only
        // (possibly empty) state over the user's cloud backup.
        setSyncedUid(authUser.uid);
      } catch {
        // Leave local untouched AND the mirror locked on failure — the next
        // mirror tick must not push over the cloud backup. A later sign-in
        // (uid change) retries the pull.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally NOT depending on the synced state slices: this captures
    // local state at sign-in time and must run once per uid, not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, hydrated, syncedUid]);

  // Back up the whole syncable slice to users/{uid} — a debounced one-way
  // mirror, gated on `syncedUid` so it only pushes AFTER the sign-in pull/merge
  // has run for this user (see above). No-ops without a uid / Firestore.
  const mirrorTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!hydrated || !authUser || syncedUid !== authUser.uid) return;
    // Don't mirror while an account deletion is in flight — the cleared local
    // slices would otherwise resurrect the just-deleted cloud doc.
    if (deletingRef.current) return;
    if (mirrorTimer.current) clearTimeout(mirrorTimer.current);
    mirrorTimer.current = setTimeout(() => {
      writeUserState(authUser.uid, {
        resumes,
        keys,
        applied,
        applications,
        alerts,
        themeMode,
        language,
        // The settings clock travels with the backup so other devices resolve
        // the scalar merge correctly (last writer of the settings wins).
        updatedAt: settingsUpdatedAt,
      });
    }, 1500);
    return () => {
      if (mirrorTimer.current) clearTimeout(mirrorTimer.current);
    };
  }, [
    resumes,
    keys,
    applied,
    applications,
    alerts,
    themeMode,
    language,
    settingsUpdatedAt,
    authUser,
    hydrated,
    syncedUid,
  ]);

  // Register this device for push once per sign-in, as soon as the user is
  // synced AND has at least one alert — so notifications work without making
  // them create another alert just to trigger the OS permission prompt. The
  // alerts.tsx "Add" flow still prompts at the moment of intent; this covers
  // the "already had alerts, then logged in" case. No-ops until the native
  // messaging module is in the build.
  const pushRegisteredUid = React.useRef<string | null>(null);
  useEffect(() => {
    if (!authUser) {
      pushRegisteredUid.current = null;
      return;
    }
    if (syncedUid !== authUser.uid) return; // wait for the cloud merge to land
    if (alerts.length === 0) return; // nothing to be notified about yet
    if (pushRegisteredUid.current === authUser.uid) return; // once per uid
    pushRegisteredUid.current = authUser.uid;
    notifications.registerForPush(authUser.uid);
  }, [authUser, syncedUid, alerts.length]);

  // RevenueCat bootstrap. Runs once after hydration. The local
  // `removeAdsEntitled` cache (read in loadInitialState) keeps ads hidden
  // through the cold-start window; this effect re-syncs with RevenueCat as
  // soon as the SDK is ready and subscribes to live updates so server-side
  // refunds / family-sharing flip the flag without a restart.
  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;
    let removeListener: (() => void) | undefined;

    (async () => {
      await initializeRevenueCat();
      if (cancelled) return;

      try {
        const entitled = await checkEntitlement();
        if (!cancelled) setRemoveAdsEntitled(entitled);
      } catch {
        /* swallow */
      }

      try {
        const lookup = await getRemoveAdsPackageWithReason();
        if (cancelled) return;
        removeAdsPackageRef.current = lookup.package;
        const price = lookup.package?.product?.priceString ?? null;
        setRemoveAdsPriceString(price);
        if (!lookup.package && lookup.reason) {
          setRemoveAdsLastError(lookup.reason);
        }
      } catch {
        /* swallow */
      }

      removeListener = addEntitlementListener((entitled) => {
        if (!cancelled) setRemoveAdsEntitled(entitled);
      });
    })();

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [hydrated]);

  const purchaseRemoveAds = useCallback(async (): Promise<{
    outcome: 'purchased' | 'cancelled' | 'failed';
    reason?: string;
  }> => {
    if (removeAdsPurchasing) return { outcome: 'failed', reason: 'already in flight' };
    setRemoveAdsLastError(null);
    let pkg = removeAdsPackageRef.current;
    if (!pkg) {
      const lookup = await getRemoveAdsPackageWithReason();
      pkg = lookup.package;
      removeAdsPackageRef.current = pkg;
      if (!pkg) {
        const reason = lookup.reason ?? 'no package available';
        setRemoveAdsLastError(reason);
        track('remove_ads_purchase_failed', { reason });
        return { outcome: 'failed', reason };
      }
    }
    setRemoveAdsPurchasing(true);
    track('remove_ads_purchase_initiated');
    try {
      const result = await purchaseRemoveAdsSdk(pkg);
      if (result.outcome === 'purchased' && result.entitled) {
        setRemoveAdsEntitled(true);
        track('remove_ads_purchase_completed');
        return { outcome: 'purchased' };
      }
      if (result.outcome === 'purchased' && !result.entitled) {
        // Purchase recorded by the store but the CustomerInfo didn't surface
        // the `remove_ads` entitlement. Almost always: the entitlement in
        // the RevenueCat dashboard isn't named `remove_ads` exactly, or the
        // purchased product isn't attached to that entitlement.
        const active = result.activeEntitlements ?? [];
        const productId = result.productIdentifier ?? 'unknown product';
        const activeStr = active.length > 0 ? active.join(', ') : 'none';
        const reason =
          `purchased "${productId}" but entitlement "remove_ads" is not active. ` +
          `Active entitlements: ${activeStr}. ` +
          `Check RevenueCat dashboard → Entitlements: the entitlement must ` +
          `be named exactly "remove_ads" and the product must be attached to it.`;
        setRemoveAdsLastError(reason);
        track('remove_ads_purchase_failed', { reason: 'entitlement_not_active' });
        return { outcome: 'failed', reason };
      }
      if (result.outcome === 'cancelled') {
        track('remove_ads_purchase_cancelled');
        return { outcome: 'cancelled' };
      }
      const reason = result.error?.message ?? 'unknown';
      setRemoveAdsLastError(reason);
      track('remove_ads_purchase_failed', { reason });
      return { outcome: 'failed', reason };
    } finally {
      setRemoveAdsPurchasing(false);
    }
  }, [removeAdsPurchasing]);

  const restoreRemoveAds = useCallback(async (): Promise<{
    outcome: 'restored' | 'empty' | 'failed';
    reason?: string;
  }> => {
    if (removeAdsPurchasing) return { outcome: 'failed', reason: 'already in flight' };
    setRemoveAdsLastError(null);
    setRemoveAdsPurchasing(true);
    track('remove_ads_restore_initiated');
    try {
      const result = await restorePurchasesSdk();
      if (result.error) {
        const reason = result.error.message;
        setRemoveAdsLastError(reason);
        track('remove_ads_restore_failed', { reason });
        return { outcome: 'failed', reason };
      }
      if (result.entitled) {
        setRemoveAdsEntitled(true);
        track('remove_ads_restore_completed');
        return { outcome: 'restored' };
      }
      track('remove_ads_restore_empty');
      return { outcome: 'empty' };
    } finally {
      setRemoveAdsPurchasing(false);
    }
  }, [removeAdsPurchasing]);

  // __DEV__-only helper. Switches RevenueCat to a new anonymous user so a
  // fresh purchase can be tested. The previous purchase stays attached to
  // the previous user in the RC dashboard.
  const resetRemoveAdsForTesting = useCallback(async (): Promise<boolean> => {
    setRemoveAdsLastError(null);
    const result = await resetRcUserForTesting();
    if (result === null) {
      setRemoveAdsLastError('reset failed — SDK not initialized');
      return false;
    }
    setRemoveAdsEntitled(result.entitled);
    return true;
  }, []);

  const handleClearFavorites = useCallback(() => {
    Alert.alert(t('alerts.eraseFavorites'), t('alerts.eraseFavoritesMessage'), [
      { text: t('common.cancel') },
      {
        text: t('common.yes'),
        onPress: () => setKeys([]),
      },
    ]);
  }, [t]);

  const handleFavorites = useCallback((job: JobType) => {
    setKeys((prev) =>
      prev.includes(job.id) ? prev.filter((k) => k !== job.id) : [...prev, job.id]
    );
  }, []);

  const handleApplied = useCallback((job: JobType) => {
    setApplied((prev) =>
      prev.includes(job.id) ? prev.filter((k) => k !== job.id) : [...prev, job.id]
    );
    // Keep the richer application record in lock-step with the applied list.
    setApplications((prev) => {
      if (prev[job.id]) {
        const next = { ...prev };
        delete next[job.id];
        return next;
      }
      const now = new Date().toISOString();
      return {
        ...prev,
        [job.id]: {
          jobId: job.id,
          status: 'applied',
          appliedAt: now,
          updatedAt: now,
          notes: '',
          stageDates: { applied: now },
          position: job.position ?? '',
          company: job.company,
          url: job.url,
        },
      };
    });
  }, []);

  const setApplicationStatus = useCallback((jobId: string, status: ApplicationStatus) => {
    setApplications((prev) => {
      const existing = prev[jobId];
      if (!existing) return prev;
      const now = new Date().toISOString();
      // Record when this stage was first reached (the user can edit it later).
      const stageDates = existing.stageDates[status]
        ? existing.stageDates
        : { ...existing.stageDates, [status]: now };
      return { ...prev, [jobId]: { ...existing, status, stageDates, updatedAt: now } };
    });
  }, []);

  const setApplicationNotes = useCallback((jobId: string, notes: string) => {
    setApplications((prev) => {
      const existing = prev[jobId];
      if (!existing) return prev;
      return { ...prev, [jobId]: { ...existing, notes, updatedAt: new Date().toISOString() } };
    });
  }, []);

  const setApplicationStageDate = useCallback(
    (jobId: string, status: ApplicationStatus, isoDate: string) => {
      setApplications((prev) => {
        const existing = prev[jobId];
        if (!existing) return prev;
        return {
          ...prev,
          [jobId]: {
            ...existing,
            stageDates: { ...existing.stageDates, [status]: isoDate },
            updatedAt: new Date().toISOString(),
          },
        };
      });
    },
    []
  );

  // Remove a recorded stage from the timeline. If it's the current status,
  // fall back to the most-recently-dated remaining stage (or 'applied').
  const removeApplicationStage = useCallback((jobId: string, status: ApplicationStatus) => {
    setApplications((prev) => {
      const existing = prev[jobId];
      if (!existing || existing.stageDates[status] === undefined) return prev;
      const stageDates = { ...existing.stageDates };
      delete stageDates[status];
      let nextStatus = existing.status;
      if (existing.status === status) {
        let best: ApplicationStatus | null = null;
        let bestTime = -Infinity;
        (Object.entries(stageDates) as [ApplicationStatus, string][]).forEach(([s, d]) => {
          const tt = Date.parse(d) || 0;
          if (tt >= bestTime) {
            bestTime = tt;
            best = s;
          }
        });
        nextStatus = best ?? 'applied';
      }
      return {
        ...prev,
        [jobId]: {
          ...existing,
          status: nextStatus,
          stageDates,
          updatedAt: new Date().toISOString(),
        },
      };
    });
  }, []);

  const removeApplication = useCallback((jobId: string) => {
    setApplied((prev) => prev.filter((id) => id !== jobId));
    setApplications((prev) => {
      if (!prev[jobId]) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
  }, []);

  // Manually-tracked application for a job found outside the app (the user
  // types company + position). Uses a `manual-` id so it never collides with
  // a scraped job id.
  const addManualApplication = useCallback((company: string, position: string) => {
    const now = new Date().toISOString();
    const jobId = `manual-${makeId()}`;
    setApplied((prev) => [...prev, jobId]);
    setApplications((prev) => ({
      ...prev,
      [jobId]: {
        jobId,
        status: 'applied',
        appliedAt: now,
        updatedAt: now,
        notes: '',
        stageDates: { applied: now },
        position: position.trim(),
        company: company.trim(),
        url: '',
      },
    }));
  }, []);

  const addAlert = useCallback((query: string, tags: string[] = []) => {
    const now = new Date().toISOString();
    const alert: JobAlert = {
      id: makeId(),
      query: query.trim(),
      tags: tags.map((t) => t.trim()).filter(Boolean),
      enabled: true,
      createdAt: now,
      updatedAt: now,
    };
    setAlerts((prev) => [alert, ...prev]);
    return alert;
  }, []);

  const updateAlert = useCallback((alert: JobAlert) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === alert.id ? { ...alert, updatedAt: new Date().toISOString() } : a))
    );
  }, []);

  const deleteAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const toggleAlert = useCallback((id: string) => {
    setAlerts((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, enabled: !a.enabled, updatedAt: new Date().toISOString() } : a
      )
    );
  }, []);

  const completeOnboarding = useCallback(() => {
    setOnboardingCompleted(true);
  }, []);

  const acknowledgeDisclaimer = useCallback(() => {
    setDisclaimerAcknowledged(true);
  }, []);

  const addResume = useCallback(
    (name: string, template: TemplateId = 'modern') => {
      const now = new Date().toISOString();
      const resume = emptyResume(makeId(), name, template, now);
      setResumes((prev) => [resume, ...prev]);
      return resume;
    },
    []
  );

  const updateResume = useCallback((resume: ResumeDocument) => {
    setResumes((prev) =>
      prev.map((r) =>
        r.id === resume.id ? { ...resume, updatedAt: new Date().toISOString() } : r
      )
    );
  }, []);

  const deleteResume = useCallback((id: string) => {
    setResumes((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const duplicateResume = useCallback((id: string) => {
    let created: ResumeDocument | null = null;
    setResumes((prev) => {
      const src = prev.find((r) => r.id === id);
      if (!src) return prev;
      const now = new Date().toISOString();
      created = {
        ...src,
        id: makeId(),
        name: `${src.name} (copy)`,
        createdAt: now,
        updatedAt: now,
      };
      return [created, ...prev];
    });
    return created;
  }, []);

  // Permanently delete the account: remove the cloud doc FIRST (while still
  // authenticated, so nothing is orphaned), then the auth account, then clear
  // the local personal slices. On failure (e.g. 'requires-recent-login') local
  // data is left intact so the user can re-auth and retry.
  const deleteAccount = useCallback(async (): Promise<{ ok: boolean; reason?: string }> => {
    const uid = authUser?.uid;
    // Lock the mirror up front: clearing the local slices below would otherwise
    // re-arm the debounced push and resurrect the doc we're about to delete,
    // before the auth listener reports sign-out. Reset on failure / sign-out.
    deletingRef.current = true;
    try {
      if (uid) await deleteUserState(uid);
      await auth.deleteAccount();
      setKeys([]);
      setApplied([]);
      setApplications({});
      setAlerts([]);
      setResumes([]);
      return { ok: true };
    } catch (e) {
      deletingRef.current = false;
      const reason = (e as { code?: string })?.code ?? 'unknown';
      return { ok: false, reason };
    }
  }, [authUser]);

  return (
    <JobsContext.Provider
      value={{
        data,
        keys,
        applied,
        applications,
        refreshing,
        error,
        onboardingCompleted,
        hydrated,
        themeMode,
        language,
        hideAds,
        removeAdsEntitled,
        disclaimerAcknowledged,
        removeAdsPurchasing,
        removeAdsPriceString,
        removeAdsLastError,
        resumes,
        refresh,
        handleClearFavorites,
        handleFavorites,
        handleApplied,
        setApplicationStatus,
        setApplicationNotes,
        setApplicationStageDate,
        removeApplicationStage,
        removeApplication,
        addManualApplication,
        alerts,
        addAlert,
        updateAlert,
        deleteAlert,
        toggleAlert,
        completeOnboarding,
        acknowledgeDisclaimer,
        setThemeMode,
        setLanguage,
        setHideAds,
        purchaseRemoveAds,
        restoreRemoveAds,
        resetRemoveAdsForTesting,
        addResume,
        updateResume,
        deleteResume,
        duplicateResume,
        deleteAccount,
      }}
    >
      {children}
    </JobsContext.Provider>
  );
}

export default { GlobalStateProvider };
